import React, { useContext, useId } from 'react'
import { Check } from 'lucide-react'
import { cn } from './cn'
import { MenuContext } from './menu-panel'

/** Menu content. Each item closes its menu, then runs `onSelect`. */

const ITEM_BASE =
  'flex w-full shrink-0 cursor-pointer items-center border-0 bg-transparent text-left text-sm outline-none transition-colors disabled:cursor-not-allowed disabled:opacity-40'

function itemGeometry(wide: boolean) {
  return wide ? 'min-h-8.5 gap-2.5 rounded-[var(--radius)] px-2 py-1.5' : 'min-h-8.5 gap-3 px-3'
}

export interface MenuItemProps extends Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  'onSelect' | 'children'
> {
  children: React.ReactNode
  icon?: React.ReactNode
  /** Right-hand muted text: a shortcut (⌘C, Delete) or a value (8 / 10 席). */
  trailing?: React.ReactNode
  /** Destroys something: deep red, always the last item (§7.7). */
  danger?: boolean
  onSelect: () => void
}

export function MenuItem({
  children,
  icon,
  trailing,
  danger = false,
  onSelect,
  className,
  ...rest
}: MenuItemProps) {
  const { wide, closeAndRefocus } = useContext(MenuContext)
  return (
    <button
      type="button"
      role="menuitem"
      tabIndex={-1}
      {...rest}
      className={cn(
        ITEM_BASE,
        itemGeometry(wide),
        danger
          ? 'text-danger hover:bg-danger-soft focus-visible:bg-danger-soft'
          : 'text-foreground hover:bg-[var(--fg-soft)] focus-visible:bg-[var(--fg-soft)]',
        className
      )}
      onClick={() => {
        // Close first: an item that opens a dialog must not lose its focus to
        // the trigger being refocused afterwards.
        closeAndRefocus()
        onSelect()
      }}
    >
      {icon != null ? (
        <span
          aria-hidden="true"
          className={cn(
            'flex size-4 shrink-0 items-center justify-center [&>svg]:size-4',
            danger ? 'text-danger' : 'text-muted'
          )}
        >
          {icon}
        </span>
      ) : null}
      <span className="min-w-0 flex-1 truncate">{children}</span>
      {trailing != null ? (
        <span className="ml-auto shrink-0 text-xs font-normal tabular-nums text-muted">
          {trailing}
        </span>
      ) : null}
    </button>
  )
}

export interface MenuItemRadioProps extends Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  'onSelect' | 'title' | 'children'
> {
  checked: boolean
  title: React.ReactNode
  description?: React.ReactNode
  /** Leading mark, e.g. a workspace's 30px tile. */
  lead?: React.ReactNode
  onSelect: () => void
}

/** One choice of several (the current workspace carries the check). */
export function MenuItemRadio({
  checked,
  title,
  description,
  lead,
  onSelect,
  className,
  ...rest
}: MenuItemRadioProps) {
  const { closeAndRefocus } = useContext(MenuContext)
  return (
    <button
      type="button"
      role="menuitemradio"
      aria-checked={checked}
      tabIndex={-1}
      {...rest}
      className={cn(
        ITEM_BASE,
        'min-h-12 gap-2.5 rounded-[var(--radius)] px-2 py-1.5 text-foreground hover:bg-[var(--fg-soft)] focus-visible:bg-[var(--fg-soft)]',
        className
      )}
      onClick={() => {
        closeAndRefocus()
        onSelect()
      }}
    >
      {lead != null ? <span className="flex shrink-0 items-center">{lead}</span> : null}
      <span className="min-w-0 flex-1">
        <span className={cn('block truncate text-md', checked ? 'font-semibold' : 'font-medium')}>
          {title}
        </span>
        {description != null ? (
          <span className="block truncate text-xs text-muted">{description}</span>
        ) : null}
      </span>
      <Check
        data-menu-check=""
        aria-hidden="true"
        className={cn('size-4.5 shrink-0', !checked && 'invisible')}
      />
    </button>
  )
}

export interface MenuGroupProps {
  /** Small muted heading above the group (「工作區」). */
  label: React.ReactNode
  children: React.ReactNode
}

export function MenuGroup({ label, children }: MenuGroupProps) {
  const headingId = useId()
  return (
    <div role="group" aria-labelledby={headingId}>
      <div id={headingId} className="px-2 pb-1 pt-1.5 text-xs text-muted">
        {label}
      </div>
      {children}
    </div>
  )
}

export function MenuSeparator() {
  const { wide } = useContext(MenuContext)
  return (
    <div role="separator" className={cn('my-1 h-px shrink-0 bg-[var(--line)]', wide && 'mx-1.5')} />
  )
}
